import React, { useState, useEffect } from 'react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import { DollarSign, ShieldAlert, TrendingDown, ThumbsDown, CheckCircle2, RefreshCw } from 'lucide-react';

export const MinPaySettingsPanel = () => {
  const [settings, setSettings] = useState({ platform_min_rate: 15, enforcement: 'warn' });
  const [analytics, setAnalytics] = useState(null);
  const [recentEvents, setRecentEvents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiCall('/api/admin/min-pay/analytics');
      setSettings(res.settings);
      setAnalytics(res.analytics);
      setRecentEvents(res.recent_events || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load minimum pay data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await apiCall('/api/admin/settings/min-pay', {
        method: 'PUT',
        body: JSON.stringify(settings),
      });
      setSettings(res.settings);
      toast.success('Minimum pay settings saved');
      load();
    } catch (e) {
      toast.error(e.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const eventLabel = {
    job_posted_below_min: { text: 'Job posted below minimum', cls: 'bg-red-50 text-red-700 border-red-200' },
    worker_acknowledged: { text: 'Worker accepted with warning', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    worker_declined: { text: 'Worker declined low pay', cls: 'bg-slate-50 text-slate-700 border-slate-200' },
  };

  return (
    <div className="space-y-6" data-testid="min-pay-panel">
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-6 text-white flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white/20 rounded-xl"><DollarSign className="w-8 h-8" /></div>
          <div>
            <h2 className="text-2xl font-bold">Minimum Pay Policy</h2>
            <p className="text-white/80">Set the platform-wide minimum hourly rate and how it's enforced</p>
          </div>
        </div>
        <button onClick={load} className="p-2 hover:bg-white/20 rounded-xl transition" data-testid="min-pay-refresh-btn">
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-5">
          <h3 className="font-bold text-slate-800">Policy Settings</h3>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Platform Minimum Hourly Rate ($/hr)</label>
            <input
              type="number"
              min="1"
              step="0.25"
              value={settings.platform_min_rate}
              onChange={(e) => setSettings({ ...settings, platform_min_rate: parseFloat(e.target.value) || 0 })}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 text-slate-900"
              data-testid="min-pay-rate-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Enforcement for Employer Job Posting</label>
            <div className="space-y-2">
              <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${settings.enforcement === 'warn' ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}>
                <input
                  type="radio"
                  checked={settings.enforcement === 'warn'}
                  onChange={() => setSettings({ ...settings, enforcement: 'warn' })}
                  className="mt-1"
                  data-testid="min-pay-enforcement-warn"
                />
                <div>
                  <p className="font-semibold text-slate-800 text-sm">Warn (allow posting)</p>
                  <p className="text-xs text-slate-500">Employers see a warning; job is flagged "Below Minimum Pay" to workers.</p>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${settings.enforcement === 'block' ? 'border-red-400 bg-red-50' : 'border-slate-200 hover:border-slate-300'}`}>
                <input
                  type="radio"
                  checked={settings.enforcement === 'block'}
                  onChange={() => setSettings({ ...settings, enforcement: 'block' })}
                  className="mt-1"
                  data-testid="min-pay-enforcement-block"
                />
                <div>
                  <p className="font-semibold text-slate-800 text-sm">Block posting</p>
                  <p className="text-xs text-slate-500">Jobs below the minimum are rejected at publish time.</p>
                </div>
              </label>
            </div>
          </div>
          <button
            onClick={save}
            disabled={saving}
            className="w-full py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition disabled:opacity-50"
            data-testid="min-pay-save-btn"
          >
            {saving ? 'Saving…' : 'Save Minimum Pay Settings'}
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center gap-2 mb-1"><ShieldAlert className="w-4 h-4 text-red-600" /><p className="text-xs font-semibold text-slate-500 uppercase">Active Below Min</p></div>
              <p className="text-3xl font-bold text-red-600" data-testid="stat-active-below-min">{analytics?.active_jobs_below_min ?? '—'}</p>
              <p className="text-xs text-slate-500">live jobs under ${settings.platform_min_rate}/hr</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center gap-2 mb-1"><TrendingDown className="w-4 h-4 text-amber-600" /><p className="text-xs font-semibold text-slate-500 uppercase">Posted Below Min</p></div>
              <p className="text-3xl font-bold text-amber-600" data-testid="stat-posted-below-min">{analytics?.jobs_posted_below_min ?? '—'}</p>
              <p className="text-xs text-slate-500">total low-pay postings</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center gap-2 mb-1"><CheckCircle2 className="w-4 h-4 text-emerald-600" /><p className="text-xs font-semibold text-slate-500 uppercase">Acknowledged</p></div>
              <p className="text-3xl font-bold text-emerald-600" data-testid="stat-acknowledged">{analytics?.worker_acknowledgments ?? '—'}</p>
              <p className="text-xs text-slate-500">workers accepted anyway</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center gap-2 mb-1"><ThumbsDown className="w-4 h-4 text-slate-600" /><p className="text-xs font-semibold text-slate-500 uppercase">Declined</p></div>
              <p className="text-3xl font-bold text-slate-700" data-testid="stat-declined">{analytics?.worker_declines ?? '—'}</p>
              <p className="text-xs text-slate-500">workers walked away</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h4 className="font-semibold text-slate-800 mb-3 text-sm">Recent Low-Pay Activity</h4>
            {recentEvents.length === 0 ? (
              <p className="text-sm text-slate-400">No low-pay events recorded yet.</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {recentEvents.map((e) => {
                  const meta = eventLabel[e.type] || { text: e.type, cls: 'bg-slate-50 text-slate-700 border-slate-200' };
                  return (
                    <div key={e._id} className={`flex items-center justify-between px-3 py-2 rounded-lg border text-xs ${meta.cls}`}>
                      <div className="min-w-0">
                        <p className="font-semibold">{meta.text}</p>
                        <p className="truncate">{e.job_title || e.job_id}{e.worker_name ? ` — ${e.worker_name}` : e.employer_name ? ` — ${e.employer_name}` : ''}</p>
                      </div>
                      <div className="text-right flex-shrink-0 ml-3">
                        <p className="font-bold">${e.pay_rate}/hr</p>
                        <p>{e.created_at ? new Date(e.created_at).toLocaleDateString() : ''}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MinPaySettingsPanel;
